import { useEffect, useRef } from "react";
import { Application, Container, Graphics } from "pixi.js";
import { hexCorners, hexToPixel, hexesInRadius, type Hex } from "./lib/hex.js";

/**
 * Pixi.js-backed renderer for an axial hex grid + two mechs.
 *
 * This is the v0 spike. Renders:
 *   - The map ring (every hex within `mapRadius`)
 *   - Mech A and Mech B as colored discs with a facing tick
 *
 * Animation is deliberately omitted from the v0; the parent updates
 * `mechA` / `mechB` and the renderer redraws. A tween layer lands when
 * the first match-state animation ships (per FE-10's coordinator-layer
 * deferral).
 *
 * Reads coordinate math from `./lib/hex.ts` — same axial dialect the
 * engine and SDKs use (FE-9).
 */

export interface MechView {
  pos: Hex;
  /** Facing enum: N=0, NE=1, SE=2, S=3, SW=4, NW=5. */
  facing: number;
  /** Hex display color (any CSS-parseable string Pixi can ingest). */
  color: string;
  /** Optional label drawn under the mech (e.g. "A" or "B"). */
  label?: string;
}

export interface HexGridRendererProps {
  /** Match map radius, in hexes. v2 bench uses 7. */
  mapRadius: number;
  mechA: MechView;
  mechB: MechView;
  /** Canvas px width. */
  width?: number;
  /** Canvas px height. */
  height?: number;
  /** Hex circumradius (corner-to-center) in px. Default 24. */
  hexSize?: number;
  /** Optional class applied to the wrapping div. */
  className?: string;
}

const PI = Math.PI;
const FACING_TO_ANGLE = (facing: number): number => {
  // 6 facings, 60° per step, 0 = North (negative Y in screen space).
  return -PI / 2 + (facing * PI) / 3;
};

function drawHexGrid(
  container: Container,
  mapRadius: number,
  hexSize: number,
  strokeColor: number,
  strokeAlpha: number,
): void {
  const g = new Graphics();
  for (const cell of hexesInRadius(mapRadius)) {
    const corners = hexCorners(cell, hexSize);
    g.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      g.lineTo(corners[i].x, corners[i].y);
    }
    g.closePath();
  }
  g.stroke({ width: 1, color: strokeColor, alpha: strokeAlpha });
  container.addChild(g);
}

function drawMech(container: Container, mech: MechView, hexSize: number): void {
  const { x, y } = hexToPixel(mech.pos, hexSize);
  const g = new Graphics();
  // Body: filled circle, stroked once.
  g.circle(x, y, hexSize * 0.55)
    .fill({ color: mech.color, alpha: 0.85 })
    .stroke({ width: 2, color: mech.color, alpha: 1 });
  // Facing tick: line from center outward, length ~hexSize.
  const angle = FACING_TO_ANGLE(mech.facing);
  g.moveTo(x, y).lineTo(x + Math.cos(angle) * hexSize * 0.95, y + Math.sin(angle) * hexSize * 0.95);
  g.stroke({ width: 3, color: mech.color, alpha: 1 });
  container.addChild(g);
}

export function HexGridRenderer({
  mapRadius,
  mechA,
  mechB,
  width = 600,
  height = 520,
  hexSize = 24,
  className,
}: HexGridRendererProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);
  const sceneRef = useRef<Container | null>(null);

  // One-shot mount: create Pixi app, attach canvas, install scene root.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    const app = new Application();
    app
      .init({
        width,
        height,
        antialias: true,
        backgroundAlpha: 0,
        // Slight devicePixelRatio bump for crisper lines on retina.
        resolution: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
        autoDensity: true,
      })
      .then(() => {
        if (disposed) {
          // Component already unmounted while init was pending. Destroy
          // now that init has completed (Pixi's destroy() is unsafe
          // before init resolves) and bail out.
          try {
            app.destroy(true, { children: true });
          } catch {
            // double-destroy under StrictMode is non-fatal
          }
          return;
        }
        host.appendChild(app.canvas);
        const scene = new Container();
        // Center the scene so q=0,r=0 sits in the middle of the canvas.
        scene.position.set(width / 2, height / 2);
        app.stage.addChild(scene);
        sceneRef.current = scene;
        // Expose the app only after init succeeds so the cleanup path
        // can't call destroy() on a half-initialized instance.
        appRef.current = app;
      })
      .catch((err) => {
        // Pixi init can fail in headless test environments — swallow
        // here so component tests don't crash on mount.
        if (typeof console !== "undefined") console.warn("Pixi init failed:", err);
      });
    return () => {
      disposed = true;
      sceneRef.current = null;
      const ready = appRef.current;
      appRef.current = null;
      if (!ready) {
        // Init still pending — the .then() block sees `disposed` and
        // tears down itself once init resolves.
        return;
      }
      if (ready.canvas?.parentElement) ready.canvas.parentElement.removeChild(ready.canvas);
      try {
        ready.destroy(true, { children: true });
      } catch {
        // double-destroy in StrictMode is non-fatal
      }
    };
  }, [width, height]);

  // Per-state redraw: clear and re-render the scene whenever inputs change.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.removeChildren();
    drawHexGrid(scene, mapRadius, hexSize, 0xffffff, 0.18);
    drawMech(scene, mechA, hexSize);
    drawMech(scene, mechB, hexSize);
  }, [mapRadius, mechA, mechB, hexSize]);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{ width, height, lineHeight: 0 }}
      aria-label="Hex grid match view"
    />
  );
}
